import { useEffect, useRef, useState } from 'react';

import { DayBlock, useSiteTime } from '@app/features/store-core';

export type DayBlockSchedulerProps = {
  /** When true, the scheduler runs and invokes the callback on dayblock change. */
  isActive?: boolean;
  /** Optional site override; defaults to current site from useSiteConfig. */
  siteId?: string;
  /** Called when the current dayblock changes (e.g. when dayblock end time is reached). */
  onChange: (dayblock: DayBlock) => Promise<void>;
};

/**
 * usedayblockScheduler
 *
 * A custom React hook that handles running logic whenever the current dayblock changes (for example, when a shift crosses into a new dayblock or a dayblock ends).
 *
 * @param {DayBlockSchedulerProps} props
 *   - isActive: boolean indicating whether the scheduler is active and should run.
 *   - siteId: (optional) ID of the site to use for determining the current dayblock; defaults to the site from useSiteConfig if not provided.
 *   - onChange: async callback function fired whenever the current dayblock changes; receives the new dayblock as its argument.
 *
 * How it works:
 *   - When activated, detects the current dayblock for the site.
 *   - Schedules the `onChange` callback to be invoked when the dayblock ends and a new one begins.
 *   - On dayblock end, reschedules itself for the next dayblock.
 *   - Cleans up timers on unmount or when deactivated to avoid memory leaks.
 *
 * Example usage:
 *   usedayblockScheduler({
 *     isActive: true,
 *     siteId: '123',
 *     onChange: async (dayblock) => {
 *       // handle dayblock change
 *     }
 *   });
 *
 * @see DayBlock
 */
export const useDayBlockScheduler = ({ isActive, siteId, ..._props }: DayBlockSchedulerProps) => {
  const siteTime = useSiteTime();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [dayblock, setDayBlock] = useState<DayBlock | undefined>();

  useEffect(() => {
    if (!siteId) return;
    const dayblock = siteTime.toDayBlockNow();
    setDayBlock(dayblock);
  }, [siteTime.toDayBlockNow]);

  // Reschedule when dayblock ends
  useEffect(() => {
    if (!isActive || !dayblock?.startTime || !siteId) return;

    const updateDayBlock = async () => {
      const today = siteTime.today();
      const diff = dayblock.endTime.getTime() - today.getTime();

      await _props.onChange(dayblock);

      // Schedule update when dayblock ends
      timeoutRef.current = setTimeout(() => {
        const newDayBlock = siteTime.toDayBlockNow();
        setDayBlock(newDayBlock);
      }, diff);
    };

    updateDayBlock();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isActive, dayblock, siteId, _props.onChange]);
};
